import React, { useCallback, useEffect, useRef, useState } from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import { createCheckoutSession, fetchPlans } from "../store/plans/plansSlice";
import {
  cancelSubscription,
  clearCancelState,
} from "../store/billing/billingSlice";
import { useToast } from "../components/ToastProvider";

function formatPlanPrice(amountCents) {
  const cents = Number(amountCents ?? 0);
  if (Number.isNaN(cents)) return "£0";
  const amount = cents / 100;
  if (amount === 0) return "Free";
  return `£${amount.toFixed(2).replace(/\.00$/, "")}`;
}

function isCurrentPlan(subscription, plan) {
  if (!subscription || !plan) return false;

  const subscriptionPlanId =
    subscription?.plan_id ?? subscription?.plan?.id ?? subscription?.planId ?? null;
  if (subscriptionPlanId !== null && subscriptionPlanId !== undefined) {
    return String(subscriptionPlanId) === String(plan?.id);
  }

  const subscriptionPlanName =
    subscription?.plan?.name ??
    subscription?.plan?.plan_title ??
    subscription?.plan_name ??
    subscription?.plan ??
    "";

  const sp = String(subscriptionPlanName).toLowerCase();
  if (!sp) return false;

  const candidates = [plan?.id, plan?.name, plan?.plan_title]
    .filter((v) => v !== undefined && v !== null)
    .map((v) => String(v).toLowerCase());

  // Use exact match only to avoid false positives (e.g. "daily" vs "monthly").
  return candidates.some((c) => c === sp);
}

function isFreeOrTrialPlan(plan) {
  const cents = Number(plan?.amount_cents ?? 0);
  const isFree = Number.isFinite(cents) && cents === 0;
  return Boolean(plan?.is_trial) || isFree;
}

function getPlanBillingCycle(plan) {
  const raw = String(
    plan?.interval_unit ||
      plan?.billing_period ||
      plan?.billing_cycle ||
      plan?.interval ||
      ""
  )
    .trim()
    .toLowerCase();

  if (!raw) return "monthly";
  if (["year", "yearly", "annual", "annually", "12_months", "12 months"].includes(raw)) {
    return "yearly";
  }
  if (["month", "monthly", "1_month", "1 month"].includes(raw)) {
    return "monthly";
  }
  if (raw.includes("year") || raw.includes("annual")) return "yearly";
  return "monthly";
}

function getPlanBudgetInfoValue(plan) {
  const raw =
    plan?.budget_info ??
    plan?.budget_value ??
    plan?.budget ??
    plan?.recommended_budget ??
    plan?.max_budget ??
    null;
  if (raw === null || raw === undefined) return "";
  const text = String(raw).trim();
  return text;
}

function getPaymentPopupFromLocation(pathname, search) {
  const normalizedPath = String(pathname || "").toLowerCase();
  if (normalizedPath.includes("/billing/success")) {
    return {
      tone: "success",
      title: "Payment Successful",
      message: "Your plan has been activated successfully.",
    };
  }
  if (normalizedPath.includes("/billing/cancel")) {
    return {
      tone: "cancel",
      title: "Payment Not Completed",
      message: "Payment was cancelled or failed. You can try again any time.",
    };
  }

  const params = new URLSearchParams(search);
  const keys = ["status", "payment", "payment_status", "checkout", "result"];

  for (const key of keys) {
    const value = (params.get(key) || "").toLowerCase();
    if (["success", "paid", "completed", "ok"].includes(value)) {
      return {
        tone: "success",
        title: "Payment Successful",
        message: "Your plan has been activated successfully.",
      };
    }
    if (["cancel", "cancelled", "canceled", "failed", "error"].includes(value)) {
      return {
        tone: "cancel",
        title: "Payment Not Completed",
        message: "Payment was cancelled or failed. You can try again any time.",
      };
    }
  }

  return null;
}

function ResultPopup({ open, title, message, tone = "success", onClose }) {
  if (!open) return null;

  const accent = tone === "success" ? "#2FC98D" : "#E44A77";
  const softBg = tone === "success" ? "bg-[#2FC98D]/10" : "bg-[#E44A77]/10";

  return (
    <div className="fixed inset-0 z-50">
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 bg-black/40"
      />

      <div className="relative mx-auto flex min-h-screen max-w-6xl items-center justify-center px-4 py-10">
        <div className="relative w-full max-w-md rounded-3xl border border-[#E6EAF0] bg-white shadow-2xl">
          <div className="relative px-6 pb-7 pt-8 text-center">
            <button
              type="button"
              onClick={onClose}
              className="absolute right-6 top-6 inline-flex h-10 w-10 items-center justify-center rounded-full border border-[#E6EAF0] bg-white p-0 text-slate-500 hover:bg-[#7352C7]/10 hover:text-femty-primary"
              aria-label="Close"
            >
              ✕
            </button>

            <div className={`mx-auto mb-5 flex h-20 w-20 items-center justify-center rounded-full border-4 ${softBg}`} style={{ borderColor: `${accent}55` }}>
              <span className="text-3xl" style={{ color: accent }}>
                {tone === "success" ? "✔" : "!"}
              </span>
            </div>

            <h3 className="text-[24px] font-semibold text-[#24303A]">{title}</h3>
            <p className="mt-2 text-[16px] font-normal text-[#6E7B86]">{message}</p>

            <button
              type="button"
              onClick={onClose}
              className="mt-7 w-full rounded-full bg-[#7352C7] py-2 text-[16px] font-bold text-white px-5"
            >
              Okay
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ConfirmCancelPopup({ open, loading, onClose, onConfirm }) {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50">
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 bg-black/40"
      />

      <div className="relative mx-auto flex min-h-screen max-w-6xl items-center justify-center px-4 py-10">
        <div className="relative w-full max-w-md rounded-3xl border border-[#E6EAF0] bg-white shadow-2xl">
          <div className="relative px-6 pb-6 pt-6">
            <button
              type="button"
              onClick={onClose}
              className="absolute right-6 top-6 inline-flex h-10 w-10 items-center justify-center rounded-full border border-[#E6EAF0] bg-white p-0 text-slate-500 hover:bg-[#7352C7]/10 hover:text-femty-primary"
              aria-label="Close"
            >
              ✕
            </button>

            <h3 className="pr-12 text-[20px] font-semibold text-[#24303A]">Cancel Subscription</h3>
            <p className="mt-2 text-[16px] font-normal text-[#6E7B86]">
              Are you sure you want to cancel? Your plan will end at the current billing period.
            </p>

            <div className="mt-6 grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={onClose}
                className="w-full rounded-full border border-[#E6EAF0] bg-white py-2 text-[16px] font-semibold text-[#24303A] "
              >
                Keep Plan
              </button>
              <button
                type="button"
                onClick={onConfirm}
                disabled={loading}
                className="w-full rounded-full bg-red-500 py-2 text-[16px] font-bold text-white disabled:cursor-not-allowed disabled:opacity-60"
              >
                {loading ? "Cancelling..." : "Yes, Cancel"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function PlanDescriptionPopup({ open, title, descriptionHtml, onClose }) {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50">
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 bg-black/40"
      />
      <div className="relative mx-auto flex min-h-screen max-w-6xl items-center justify-center px-4 py-10">
        <div className="relative w-full max-w-2xl rounded-3xl border border-[#E6EAF0] bg-white shadow-2xl">
          <div className="px-6 pb-6 pt-6">
            <div className="flex items-start justify-between gap-3">
              <h3 className="text-[20px] font-semibold text-[#24303A]">{title || "Plan details"}</h3>
              <button
                type="button"
                onClick={onClose}
                className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-[#E6EAF0] bg-white p-0 text-slate-500 hover:bg-[#7352C7]/10 hover:text-femty-primary"
                aria-label="Close"
              >
                ✕
              </button>
            </div>
            <div
              className="mt-4 max-h-[55vh] overflow-y-auto rounded-xl border border-[#E6EAF0] bg-slate-50 px-4 py-3 text-[16px] leading-relaxed text-[#6E7B86]"
              dangerouslySetInnerHTML={{ __html: descriptionHtml || "" }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function Subscription({ onOpenAuth, onOpenMenu }) {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const plans = useAppSelector((s) => s.plans.items);
  const status = useAppSelector((s) => s.plans.status);
  const error = useAppSelector((s) => s.plans.error);
  const checkoutStatus = useAppSelector((s) => s.plans.checkoutStatus);
  const token = useAppSelector((s) => s.auth.token);
  const user = useAppSelector((s) => s.auth.user);
  const cancelStatus = useAppSelector((s) => s.billing?.cancelStatus);
  const cancelError = useAppSelector((s) => s.billing?.cancelError);

  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [billingCycle, setBillingCycle] = useState("monthly");
  const [planDescriptionPopup, setPlanDescriptionPopup] = useState({
    open: false,
    title: "",
    descriptionHtml: "",
  });
  const [resultPopup, setResultPopup] = useState({
    open: false,
    tone: "success",
    title: "",
    message: "",
  });

  const activePlanFromApi = Array.isArray(plans)
    ? plans.find((p) => p?.current_active_subscription === true)
    : null;

  // Build a "billing subscription shape" from the plans API response so the UI
  // can render without calling `api/billing/subscription`.
  const subscriptionState = activePlanFromApi
    ? {
        plan_id: activePlanFromApi?.id ?? null,
        status: "active",
        cancel_at_period_end: Boolean(activePlanFromApi?.cancel_at_period_end),
        current_period_end: activePlanFromApi?.current_period_end ?? null,
        plan: {
          plan_id: activePlanFromApi?.id ?? null,
          id: activePlanFromApi?.id ?? null,
          name: activePlanFromApi?.name ?? null,
          plan_title: activePlanFromApi?.plan_title ?? null,
          plan_name: activePlanFromApi?.name ?? null,
        },
      }
    : null;

  const refreshPlansTimerRef = useRef(null);
  const schedulePlansRefresh = useCallback(() => {
    if (refreshPlansTimerRef.current) clearTimeout(refreshPlansTimerRef.current);
    // Webhook/back-end update can be slightly delayed; fetch once after 2.5s.
    refreshPlansTimerRef.current = setTimeout(() => {
      dispatch(fetchPlans({ force: true }));
      refreshPlansTimerRef.current = null;
    }, 2500);
  }, [dispatch]);

  useEffect(() => {
    return () => {
      if (refreshPlansTimerRef.current) clearTimeout(refreshPlansTimerRef.current);
      refreshPlansTimerRef.current = null;
    };
  }, []);

  const currentPlan =
    Array.isArray(plans) && plans.length > 0
      ? plans.find((p) => isCurrentPlan(subscriptionState?.plan, p))
      : null;

  const hasPaidCurrentPlan = !!currentPlan && !isFreeOrTrialPlan(currentPlan);
  const hasActiveSubscription = Boolean(token && subscriptionState?.status);
  const isOnTrialOrFree = Boolean(currentPlan) && isFreeOrTrialPlan(currentPlan);

  const visiblePlans = (Array.isArray(plans) ? plans : []).filter((p) => {
    if (getPlanBillingCycle(p) !== billingCycle) return false;
    // Trial/free plan is auto-activated at signup — only show it to logged-out visitors.
    if (token && isFreeOrTrialPlan(p)) return false;
    return true;
  });

  useEffect(() => {
    const pathname = String(location.pathname || "");
    if (!pathname.toLowerCase().startsWith("/subscription")) return;
    // Ensure plans are refetched whenever user navigates to this page.
    dispatch(fetchPlans({ force: true }));
  }, [location.pathname, dispatch]);

  useEffect(() => {
    if (!token) return;
    // User may land here logged-out, then login via modal.
    // Refetch plans after token is set so `current_active_subscription` updates immediately.
    dispatch(fetchPlans({ force: true }));
  }, [token, dispatch]);

  useEffect(() => {
    const popup = getPaymentPopupFromLocation(location.pathname, location.search);
    if (!popup) return;

    // If user is not logged in, persist the intent and prompt login.
    // After login, we reopen the popup and refetch subscription automatically (no manual refresh needed).
    if (!token) {
      sessionStorage.setItem("pending_billing_popup", JSON.stringify(popup));
      if (popup.tone === "success") sessionStorage.setItem("pending_billing_success", "1");
      onOpenAuth?.("login");
      return;
    }

    setResultPopup({ open: true, ...popup });
    showToast({
      type: popup.tone === "success" ? "success" : "error",
      title: popup.title,
      message: popup.message,
    });
    if (popup.tone === "success") {
      schedulePlansRefresh();
      sessionStorage.removeItem("pending_billing_success");
    }

    // Normalize URL after Stripe redirect, but keep UI state intact.
    const normalizedPath = String(location.pathname || "").toLowerCase();
    if (normalizedPath.includes("/billing/success") || normalizedPath.includes("/billing/cancel")) {
      navigate("/subscription", { replace: true });
    }
  }, [
    token,
    location.pathname,
    location.search,
    navigate,
    onOpenAuth,
    schedulePlansRefresh,
    showToast,
  ]);

  useEffect(() => {
    if (!token) return;
    if (sessionStorage.getItem("pending_billing_success") !== "1") return;
    schedulePlansRefresh();
    sessionStorage.removeItem("pending_billing_success");
  }, [token, schedulePlansRefresh]);

  useEffect(() => {
    if (!token) return;
    const raw = sessionStorage.getItem("pending_billing_popup");
    if (!raw) return;
    try {
      const popup = JSON.parse(raw);
      if (popup?.title && popup?.message) {
        setResultPopup({ open: true, ...popup });
        showToast({
          type: popup.tone === "success" ? "success" : "error",
          title: popup.title,
          message: popup.message,
        });
      }
    } catch {
      // ignore
    } finally {
      sessionStorage.removeItem("pending_billing_popup");
    }
  }, [token, showToast]);

  useEffect(() => {
    if (cancelStatus !== "succeeded") return;

    setShowCancelConfirm(false);
    setResultPopup({
      open: true,
      tone: "success",
      title: "Cancellation Requested",
      message: "Your subscription has been marked to cancel at period end.",
    });
    showToast({
      type: "success",
      title: "Cancellation Requested",
      message: "Your subscription has been marked to cancel at period end.",
    });

    schedulePlansRefresh();
    dispatch(clearCancelState());
  }, [cancelStatus, dispatch, showToast, schedulePlansRefresh]);

  async function handleGetStarted(planId) {
    if (!token) {
      onOpenAuth?.("login");
      return;
    }

    const plan = Array.isArray(plans) ? plans.find((p) => p?.id === planId) : null;
    if (plan && isFreeOrTrialPlan(plan)) {
      return;
    }

    setSelectedPlanId(planId);
    try {
      const session = await dispatch(createCheckoutSession(planId)).unwrap();
      if (session?.url) {
        window.location.href = session.url;
        return;
      }
      setResultPopup({
        open: true,
        tone: "cancel",
        title: "Checkout Error",
        message: "Checkout session created, but no URL was returned.",
      });
      showToast({
        type: "error",
        title: "Checkout Error",
        message: "Checkout session created, but no URL was returned.",
      });
    } catch (e) {
      showToast({
        type: "error",
        title: "Checkout Failed",
        message: typeof e === "string" ? e : "Could not start checkout. Please try again.",
      });
    } finally {
      setSelectedPlanId(null);
    }
  }

  async function handleConfirmCancel() {
    try {
      await dispatch(cancelSubscription()).unwrap();
    } catch (e) {
      // Error is displayed inline below.
      showToast({
        type: "error",
        title: "Cancellation Failed",
        message: typeof e === "string" ? e : "Could not cancel subscription. Please try again.",
      });
    }
  }

  useEffect(() => {
    if (!cancelError) return;
    showToast({ type: "error", title: "Cancellation Error", message: cancelError });
  }, [cancelError, showToast]);

  function getCtaLabel(plan, isCurrent, canRenewCurrentPlan) {
    if (isCurrent) return canRenewCurrentPlan ? "Renew" : "Current Plan";
    if (checkoutStatus === "loading" && selectedPlanId === plan?.id) {
      return "Redirecting...";
    }

  const isFreePlan = Number(plan?.amount_cents ?? 0) === 0;
  if (isFreePlan) return "Start Saving Free";
  if (isFreeOrTrialPlan(plan)) return "Start 7-Day Free Trial";
    if (hasActiveSubscription && hasPaidCurrentPlan) return "Upgrade";
    if (hasActiveSubscription && isOnTrialOrFree) return "Upgrade";

  return "Start 7-Day Free Trial";
  }

  return (
    <div className="min-h-screen bg-[#F5F7FB] text-[#24303A] flex flex-col">
      <Header onOpenAuth={onOpenAuth} onOpenMenu={onOpenMenu} />

      <main className="mx-auto w-full max-w-7xl flex-1 px-4 py-10 md:px-10">
        <section className="relative overflow-hidden rounded-3xl border border-[#E6EAF0] bg-white px-6 py-10 text-center shadow-sm md:px-10">
          <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(900px_280px_at_50%_-40px,rgba(115,82,199,0.18),transparent_60%)]" />
          <div className="relative">
            <h1 className="text-[28px] font-semibold text-[#24303A]">Compare supermarket prices and save on every shop            </h1>
            <p className="mt-2 text-[16px] font-normal text-[#6E7B86]">
            Build your basket and see the cheapest total across supermarkets in seconds.
            </p>
            <p className="mt-2 text-[16px] font-normal text-[#6E7B86]">Start free comparison. Upgrade to unlock smarter savings and full comparisons</p>
            <div className="mt-5 inline-flex items-center rounded-full border border-[#E6EAF0] bg-white p-1 shadow-sm">
              <button
                type="button"
                onClick={() => setBillingCycle("monthly")}
                className={[
                  "rounded-full px-4 py-1.5 text-[14px] font-semibold transition",
                  billingCycle === "monthly"
                    ? "bg-[#7352C7] text-white"
                    : "text-[#6E7B86]",
                ].join(" ")}
              >
                Monthly
              </button>
              <button
                type="button"
                onClick={() => setBillingCycle("yearly")}
                className={[
                  "rounded-full px-4 py-1.5 text-[14px] font-semibold transition",
                  billingCycle === "yearly"
                    ? "bg-[#7352C7] text-white"
                    : "text-[#6E7B86]",
                ].join(" ")}
              >
                Yearly
              </button>
            </div>
          </div>
        </section>

        {cancelError && (
          <section className="mt-6">
            {cancelError && (
              <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-[12px] text-red-700 whitespace-pre-line">
                {cancelError}
              </div>
            )}
          </section>
        )}

        {status === "loading" ? (
          <section className="mt-8 flex items-center justify-center">
            <span className="text-[16px] text-slate-500">Loading plans...</span>
          </section>
        ) : error ? (
          <section className="mt-8">
            <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-[16px] text-red-700 whitespace-pre-line">
              {error}
            </div>
          </section>
        ) : visiblePlans.length === 0 ? (
          <section className="mt-8">
            <div className="text-[16px] text-slate-500">
              No {billingCycle} plans available.
            </div>
          </section>
        ) : (
          <section className="mt-8 grid gap-6 md:grid-cols-3">
            {visiblePlans.map((plan) => {
              const priceLabel = formatPlanPrice(plan.amount_cents);
              const budgetInfoValue = getPlanBudgetInfoValue(plan);
              const intervalUnit =
                String(plan?.interval);
              const descriptionText =
                typeof plan.description === "string" && plan.description.trim()
                  ? plan.description.trim()
                  : plan.plan_title || "";
              const isCurrent = isCurrentPlan(subscriptionState, plan);
              const currentPeriodEndLabel = subscriptionState?.current_period_end
                ? new Date(subscriptionState.current_period_end).toLocaleDateString()
                : null;
              const isTrial = Boolean(plan?.is_trial);
              const freeSearches = Number(plan?.free_searches ?? 0);
              const isFree = Number(plan?.amount_cents ?? 0) === 0;
              const canRenewCurrentPlan =
                Boolean(isCurrent && subscriptionState?.cancel_at_period_end) &&
                !isFreeOrTrialPlan(plan);

              return (
                <article
                  key={plan.id}
                  className={[
                    "relative flex flex-col justify-between overflow-hidden rounded-2xl border bg-white px-6 py-6 text-left shadow-sm transition",
                    "hover:-translate-y-0.5 hover:shadow-md",
                    isCurrent
                      ? "border-[#7352C7]/30 ring-1 ring-[#7352C7]/20"
                      : "border-[#E6EAF0]",
                  ].join(" ")}
                >
                  <div className="pointer-events-none absolute inset-x-0 top-0 h-24 bg-[radial-gradient(520px_180px_at_20%_0%,rgba(115,82,199,0.14),transparent_60%)]" />
                  {budgetInfoValue && (
                    <span className="absolute right-4 top-4 z-10 inline-flex max-w-[120px] items-center truncate whitespace-nowrap rounded-full bg-[#7352C7]/10 px-2.5 py-1 text-[11px] font-semibold text-[#7352C7] ring-1 ring-[#7352C7]/20">
                      {budgetInfoValue}
                    </span>
                  )}
                  <div>
                    <div className="flex items-start justify-between gap-3">
                      <div className="relative min-h-[92px] pr-24">
                        <h2 className="text-[16px] font-medium uppercase tracking-[0.12em] text-[#6E7B86]">
                          {plan.name || "Plan"}
                        </h2>
                        <p className="mt-1 text-[20px] font-semibold text-[#24303A]">
                          {plan.plan_title || plan.name || "Subscription"}
                        </p>
                      </div>
                    </div>

                    <p className="relative mt-4 min-h-[44px] text-[28px] font-semibold text-[#24303A]">
                      {priceLabel}
                      {Number(plan?.amount_cents ?? 0) > 0 && (
                        <span className="ml-2 text-[16px] font-normal text-[#6E7B86]">
                          /{intervalUnit}
                        </span>
                      )}
                    </p>

                    {/* <div className="relative mt-4 flex min-h-[36px] flex-wrap items-start gap-2">
                      {(isFree || isTrial) && (
                        <span className="inline-flex items-center rounded-full bg-emerald-50 px-2.5 py-1 text-[16px] font-semibold text-emerald-700 ring-1 ring-emerald-100">
                          {isTrial ? "Trial" : "Free"}
                        </span>
                      )}
                      {Number.isFinite(freeSearches) && freeSearches > 0 && (
                        <span className="inline-flex items-center rounded-full bg-slate-50 px-2.5 py-1 text-[16px] font-semibold text-slate-700 ring-1 ring-[#E6EAF0]">
                          {freeSearches} free searches
                        </span>
                      )}
                    </div> */}

                    {isCurrent && subscriptionState && (
                      <div className="relative mt-4 rounded-xl border border-[#E6EAF0] bg-slate-50 px-4 py-3 text-[16px] text-slate-700">
                        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                          <span className="font-semibold text-slate-700">Subscription</span>
                          <span className="text-slate-300">•</span>
                          <span className="rounded-full capitalize bg-white px-2 py-0.5 text-[16px] font-semibold text-slate-700 ring-1 ring-[#E6EAF0]">
                            {subscriptionState?.cancel_at_period_end
                              ? "expired"
                              : (subscriptionState.status || "—")}
                          </span>
                          {currentPeriodEndLabel && (
                            <>
                              <span className="text-slate-300">•</span>
                              <span className="text-slate-600">
                                {subscriptionState?.cancel_at_period_end ? (
                                  <>
                                    Valid till{" "}
                                    <span className="font-semibold text-slate-800">
                                      {currentPeriodEndLabel}
                                    </span>
                                  </>
                                ) : (
                                  <>
                                    Renews on{" "}
                                    <span className="font-semibold text-slate-800">
                                      {currentPeriodEndLabel}
                                    </span>
                                  </>
                                )}
                              </span>
                            </>
                          )}
                        </div>
                        {!subscriptionState.cancel_at_period_end && !isFreeOrTrialPlan(plan) && (
                          <button
                            type="button"
                            className="mt-3 inline-flex items-center rounded-full bg-red-100 px-3 py-1.5 text-[16px] font-semibold text-red-500"
                            onClick={() => setShowCancelConfirm(true)}
                          >
                            Cancel Subscription
                          </button>
                        )}
                      </div>
                    )}

                    {!!descriptionText && (
                      <div className="mt-3 text-left">
                        <div
                          className="h-32 overflow-hidden pr-1 text-[16px] text-[#6E7B86] overflow-y-auto"
                          dangerouslySetInnerHTML={{ __html: descriptionText }}
                        />
                        <div className="mt-2 h-7">
                          <button
                            type="button"
                            onClick={() =>
                              setPlanDescriptionPopup({
                                open: true,
                                title: plan.plan_title || plan.name || "Plan details",
                                descriptionHtml: descriptionText,
                              })
                            }
                            className="inline-flex h-7 w-fit items-center justify-start rounded-full border border-[#E6EAF0] bg-white px-3 text-left text-[14px] font-semibold text-[#7352C7]"
                          >
                            View details
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  <button
                    type="button"
                    className={[
                      "mt-6 w-full rounded-full px-4 py-2 text-[16px] font-bold shadow-sm transition focus:outline-none focus:ring-2 focus:ring-[#7352C7]/25",
                      isCurrent && !canRenewCurrentPlan
                        ? "cursor-not-allowed bg-slate-100 text-slate-500"
                        : "bg-[#7352C7] text-white",
                    ].join(" ")}
                    onClick={() => handleGetStarted(plan.id)}
                    disabled={
                      (isCurrent && !canRenewCurrentPlan) ||
                      (checkoutStatus === "loading" && selectedPlanId === plan.id)
                    }
                  >
                    {getCtaLabel(plan, isCurrent, canRenewCurrentPlan)}
                  </button>
                </article>
              );
            })}
          </section>
        )}

      </main>

      <Footer />

      <ConfirmCancelPopup
        open={showCancelConfirm}
        loading={cancelStatus === "loading"}
        onClose={() => {
          setShowCancelConfirm(false);
          dispatch(clearCancelState());
        }}
        onConfirm={handleConfirmCancel}
      />

      <ResultPopup
        open={resultPopup.open}
        tone={resultPopup.tone}
        title={resultPopup.title}
        message={resultPopup.message}
        onClose={() => setResultPopup({ open: false, tone: "success", title: "", message: "" })}
      />

      <PlanDescriptionPopup
        open={planDescriptionPopup.open}
        title={planDescriptionPopup.title}
        descriptionHtml={planDescriptionPopup.descriptionHtml}
        onClose={() => setPlanDescriptionPopup({ open: false, title: "", descriptionHtml: "" })}
      />
    </div>
  );
}

export default Subscription;
